import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PasswordField } from "@/components/ui/PasswordField";
import { ApiError } from "@/lib/api";
import {
  useChangeOwnPassword,
  useLogout,
  useMe,
  useUpdateOwnProfile,
} from "@/queries/auth";

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { data: me } = useMe();
  const updateProfile = useUpdateOwnProfile();
  const changePassword = useChangeOwnPassword();
  const logout = useLogout();

  const [displayName, setDisplayName] = useState(me?.display_name ?? "");
  const [profileSaved, setProfileSaved] = useState(false);

  useEffect(() => {
    if (me) setDisplayName(me.display_name ?? "");
  }, [me]);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [passwordErrorKey, setPasswordErrorKey] = useState<string | null>(null);
  const [passwordSaved, setPasswordSaved] = useState(false);

  if (!me) return null;

  const onProfileSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setProfileSaved(false);
    const next = displayName.trim() === "" ? null : displayName.trim();
    updateProfile.mutate(
      { display_name: next },
      { onSuccess: () => setProfileSaved(true) },
    );
  };

  const onPasswordSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasswordErrorKey(null);
    setPasswordSaved(false);

    if (newPassword.length < 8) {
      setPasswordErrorKey("tooShort");
      return;
    }
    if (newPassword !== confirm) {
      setPasswordErrorKey("mismatch");
      return;
    }

    changePassword.mutate(
      { current_password: currentPassword, new_password: newPassword },
      {
        onSuccess: () => {
          setPasswordSaved(true);
          setCurrentPassword("");
          setNewPassword("");
          setConfirm("");
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 400) {
            setPasswordErrorKey("currentWrong");
          } else {
            setPasswordErrorKey("generic");
          }
        },
      },
    );
  };

  const onSignOut = () => {
    logout.mutate(undefined, {
      onSettled: () => {
        void navigate({ to: "/login" });
      },
    });
  };

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("title")}</h1>
        <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t("profile.heading")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="flex flex-col gap-4" onSubmit={onProfileSubmit}>
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-email">{t("profile.emailLabel")}</Label>
              <Input id="settings-email" type="email" value={me.email} disabled readOnly />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-role">{t("profile.roleLabel")}</Label>
              <Input id="settings-role" value={me.role} disabled readOnly />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-display-name">
                {t("profile.displayNameLabel")}
              </Label>
              <Input
                id="settings-display-name"
                name="display_name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={120}
                placeholder={t("profile.displayNamePlaceholder")}
                disabled={updateProfile.isPending}
              />
            </div>
            {profileSaved && (
              <Alert>
                <AlertDescription>{t("profile.savedToast")}</AlertDescription>
              </Alert>
            )}
            <div>
              <Button type="submit" disabled={updateProfile.isPending}>
                {updateProfile.isPending ? t("profile.saving") : t("profile.save")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("password.heading")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="flex flex-col gap-4" onSubmit={onPasswordSubmit} noValidate>
            <div className="flex flex-col gap-2">
              <Label htmlFor="pw-current">{t("password.currentLabel")}</Label>
              <PasswordField
                id="pw-current"
                name="current_password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={changePassword.isPending}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="pw-new">{t("password.newLabel")}</Label>
              <PasswordField
                id="pw-new"
                name="new_password"
                autoComplete="new-password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={changePassword.isPending}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="pw-confirm">{t("password.confirmLabel")}</Label>
              <PasswordField
                id="pw-confirm"
                name="confirm_password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={changePassword.isPending}
              />
            </div>
            {passwordErrorKey && (
              <Alert variant="destructive">
                <AlertDescription>
                  {t(`password.errors.${passwordErrorKey}`)}
                </AlertDescription>
              </Alert>
            )}
            {passwordSaved && (
              <Alert>
                <AlertDescription>{t("password.success")}</AlertDescription>
              </Alert>
            )}
            <div>
              <Button
                type="submit"
                disabled={
                  changePassword.isPending || !currentPassword || !newPassword || !confirm
                }
              >
                {changePassword.isPending ? t("password.submitting") : t("password.submit")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("session.heading")}</CardTitle>
          <CardDescription>{me.email}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={onSignOut} disabled={logout.isPending}>
            {t("session.signOut")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
